import React, { useEffect, useState, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import fetchClient from './api/configuration';
import reportWebVitals from './reportWebVitals';
import './assets/css/base.scss';
import ErrorBoundary from './components/Error';
import ConsignorHome from './components/ConsignorHome';
import Header from './components/Header';
import Footer from './components/Footer';
import Login from './components/Login';
import Logout from './components/Logout';
import OAuthCallback from './components/OAuthCallback';
import NotFound from './components/NotFound';
import store from './reducers/index';

// Decode a JWT payload without verifying signature (client-side only).
// Used to check token expiry so the UI reacts to expired/revoked sessions.
function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch (_) {
    return null;
  }
}

const SESSION_POLL_INTERVAL_MS = 30 * 1000; // 30 seconds

// Protected route component
function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    const token = sessionStorage.getItem('id_token');
    if (!token) {
      setIsAuthenticated(false);
      return undefined;
    }
    const expiry = getTokenExpiry(token);
    if (expiry && Date.now() > expiry) {
      sessionStorage.removeItem('id_token');
      setIsAuthenticated(false);
      return undefined;
    }
    setIsAuthenticated(true);

    // Poll /api/auth/session so the backend can kick us out immediately after
    // a front-channel logout (Entra posts the revoked sid to the backend).
    // The axios 401 interceptor in configuration.js handles the redirect to login.
    pollRef.current = setInterval(() => {
      if (!sessionStorage.getItem('id_token')) return;
      fetchClient.get('auth/session').catch(() => {});
    }, SESSION_POLL_INTERVAL_MS);

    return () => clearInterval(pollRef.current);
  }, []);

  if (isAuthenticated === null) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/manheimsupport/login" replace />;
  }

  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/manheimsupport/login" element={<Login />} />
          <Route path="/manheimsupport/logout" element={<Logout />} />
          <Route path="/manheimsupport/callback" element={<OAuthCallback />} />
          <Route
            path="/manheimsupport/index"
            element={
              <ProtectedRoute>
                <div id="pageFrame">
                  <Header />
                  <ConsignorHome />
                  <Footer />
                </div>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/manheimsupport/index" replace />} />
          <Route
            path="*"
            element={
              <div id="pageFrame">
                <Header />
                <NotFound />
                <Footer />
              </div>
            }
          />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <Provider store={store}>
    <App />
  </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
